import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';

function HacemosUI({
  currentTranslations,
  testimoniosData,
  icons,
  colors,
  visibleItems,
  itemRefs,
}) {
  return (
    <section className="relative py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-40 h-40 sm:w-56 sm:h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-gradient-to-r from-orange-500/5 to-red-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-primary bg-clip-text mb-6 sm:mb-8 leading-tight px-4">
            {currentTranslations.whatWeDo}
          </h2>

          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10">
            <div className="w-12 sm:w-16 h-1 bg-gradient-to-r from-green-500 to-gray-600 rounded-full"></div>
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r from-green-500 to-gray-600 rounded-full"></div>
            <div className="w-12 sm:w-16 h-1 bg-gradient-to-r from-green-500 to-gray-600 rounded-full"></div>
          </div>

          <p className="text-base sm:text-lg md:text-xl max-w-5xl mx-auto leading-relaxed font-light px-4">
            {currentTranslations.whatWeDoDescription}
          </p>
        </div>

        {/* Timeline Section */}
        <div className="relative">
          {/* Vertical Line - Hidden on mobile, visible on md+ */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-green-600 rounded-full"></div>

          <div className="space-y-12 sm:space-y-16 md:space-y-24">
            {testimoniosData.map((testimonio, index) => {
              const icon = icons[index];
              const colorClass = colors[index];
              const isVisible = visibleItems.includes(index);
              const isLeft = index % 2 === 0;

              return (
                <div
                  key={testimonio.id}
                  ref={(el) => (itemRefs.current[index] = el)}
                  data-index={index}
                  className={`relative transition-all duration-1000 delay-${index * 200} ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
                  }`}
                >
                  {/* Center Icon - Responsive positioning */}
                  <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 md:top-1/2 flex justify-center md:block mb-6 md:mb-0 z-20">
                    <div className="relative">
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-r ${colorClass} rounded-full flex items-center justify-center shadow-2xl border-2 sm:border-4 border-gray-900 hover:scale-110 md:hover:scale-125 transition-all duration-300`}>
                        <FontAwesomeIcon icon={icon} className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                      </div>
                      <div className={`absolute inset-0 bg-gradient-to-r ${colorClass} rounded-full animate-ping opacity-20`}></div>
                    </div>
                  </div>

                  {/* Content Card - Responsive layout */}
                  <div className={`flex items-center justify-center ${isLeft ? 'md:justify-start' : 'md:justify-end'}`}>
                    <div className={`w-full md:max-w-lg ${isLeft ? 'md:mr-auto md:pr-12 lg:pr-16' : 'md:ml-auto md:pl-12 lg:pl-16'}`}>
                      <div className="bg-gray-800/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-2xl border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300 group">
                        {/* Number Badge */}
                        <div className={`inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r ${colorClass} rounded-lg text-white font-bold text-xs sm:text-sm mb-4 sm:mb-6`}>
                          {index + 1}
                        </div>

                        {/* Title */}
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4 group-hover:text-gray-100 transition-colors duration-300">
                          {testimonio.title}
                        </h3>

                        {/* Content */}
                        <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6">
                          {testimonio.content}
                        </p>

                        {/* Image */}
                        <div className="relative overflow-hidden rounded-xl sm:rounded-2xl mb-0 group-hover:scale-105 transition-transform duration-500">
                          <img
                            src={testimonio.image}
                            alt={testimonio.title}
                            className="w-full h-40 sm:h-48 md:h-56 object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down - Only visible on desktop */}
                  {index < testimoniosData.length - 1 && (
                    <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 bottom-0 translate-y-12 z-10">
                      <FontAwesomeIcon icon={faArrowDown} className="w-5 h-5 md:w-6 md:h-6 text-gray-500 animate-bounce" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default HacemosUI;